import { SxProps, Theme } from "@mui/material";

/**
 * The row layouts this directory's fields share.
 *
 * They share them because the design prototype does: it builds every row from the same handful of
 * style objects, so keeping the values in one place here is what stops the rows drifting apart as any
 * one of them is tuned.
 *
 *   fieldRowStyle:  { display: flex, gap: 10px, alignItems: center }   // phone: column, gap 16
 *   narrowColStyle: { flex: "0 0 30%" }
 *   splitStyle:     { display: grid, gridTemplateColumns: "2fr 1fr", gap: 16 }  // phone: 1fr
 */

/**
 * `flex-start` and not the prototype's `center`, which was right until phase 3 gave these rows error
 * messages. A MUI `TextField` renders its helper text *inside* the field, so one field on a row
 * growing by a line of copy makes that field taller than its neighbours — and centring then drops
 * every other input on the row by half that line. The inputs are what the eye lines up, so they are
 * what stays aligned; the message hangs below its own field.
 *
 * The same reason `entityFieldLayout.ts` sets `alignItems: "start"` on the editor grid.
 */
export const FIELD_ROW_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "stretch", sm: "flex-start" },
  gap: { xs: 2, sm: 1.25 },
};

/**
 * The type select on both rows. **25%, narrower than the prototype's 30%** — asked for on 2026-08-12,
 * on the grounds that the option labels are short next to the date and address fields sharing the row.
 *
 * `minWidth: 0` because a flex item's default `min-width: auto` lets its content override the basis
 * (README decision 17). The longest labels — "Frequented Address", "Safe House Address" — sit close to
 * the width this leaves, so they are the ones to look at before narrowing it further; the menu always
 * shows them in full.
 */
export const TYPE_COL_SX: SxProps<Theme> = {
  flex: { sm: "0 0 25%" },
  minWidth: 0,
};

/**
 * A wide field beside a narrow one — contact name beside badge #, phone beside extension. A grid
 * rather than the flex row above because both halves are plain inputs of the same height, and the
 * ratio is what carries the meaning.
 */
export const SPLIT_ROW_SX: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
  gap: 2,
};

/**
 * The remove button, kept out of the flex distribution and dropped below the fields on a phone.
 *
 * `flex-start` for the reason `FIELD_ROW_SX` gives, and it needs no offset to line up: a `size="small"`
 * outlined input and a default `IconButton` are both 40px tall.
 */
export const ROW_DELETE_SX: SxProps<Theme> = {
  flexShrink: 0,
  alignSelf: { xs: "flex-end", sm: "flex-start" },
};
